import {axiosInstance} from 'api/axiosFuncs';
import logoSrc from 'res/img/US_tag_RGB.png';
import {LOGIN_URL} from 'utils/consts';

const LOGIN_SUCCESS_EVENT = 'track:login-success';

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;

const openPopupTo = (url) => {
  const left = Math.max(0, (window.screen.width - POPUP_WIDTH) / 2);
  const top = Math.max(0, (window.screen.height - POPUP_HEIGHT) / 2);
  return window.open(
    url,
    'track_login',
    `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
};

const attachMessageListener = (popup) => {
  const handleMessage = (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== 'LOGIN_SUCCESS') return;

    window.removeEventListener('message', handleMessage);
    sessionStorage.removeItem('login_popup_nonce'); // cleanup for any old keys
    window.dispatchEvent(
      new CustomEvent(LOGIN_SUCCESS_EVENT, {detail: {data: event.data.data}})
    );
  };
  window.addEventListener('message', handleMessage);

  // Clean up the listener if the popup is closed without completing login
  const pollClosed = setInterval(() => {
    if (popup.closed) {
      clearInterval(pollClosed);
      window.removeEventListener('message', handleMessage);
    }
  }, 500);
};

const buildLoadingContent = (doc) => {
  const logoUrl = `${window.location.origin}${logoSrc}`;

  doc.body.style.cssText =
    'font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#fff';

  const wrapper = doc.createElement('div');
  wrapper.style.textAlign = 'center';

  const img = doc.createElement('img');
  img.src = logoUrl;
  img.alt = 'Uncommon Schools';
  img.style.cssText =
    'max-width:180px;margin-bottom:20px;display:block;margin-left:auto;margin-right:auto';
  img.onerror = () => {
    img.style.display = 'none';
  };

  const text = doc.createElement('div');
  text.style.cssText = 'color:#555;font-size:14px';
  text.textContent = 'Redirecting to OneLogin\u2026';

  wrapper.appendChild(img);
  wrapper.appendChild(text);
  doc.body.appendChild(wrapper);
};

/**
 * Opens the OAuth login flow in a popup window so the current page keeps its state.
 *
 * Fetches the OneLogin redirect URL from the backend first (in the parent window,
 * where the auth token state is known), then navigates the popup directly to OneLogin.
 * This avoids loading the React app inside the popup entirely.
 *
 * Falls back to full-page navigation to /login when enabled and the popup flow cannot continue.
 */
export const openLoginPopup = (options = {}) => {
  const fallbackMode = options?.fallbackMode || 'always';
  const fallbackToLogin = (reason) => {
    const shouldFallback =
      fallbackMode === 'always' ||
      (fallbackMode === 'blocked' && reason === 'blocked');
    if (shouldFallback) {
      window.location.href = '/login';
    }
  };

  // Open a blank popup immediately (must be in the same synchronous call stack
  // as the user gesture to avoid popup blockers)
  let popup = openPopupTo('about:blank');

  if (!popup) {
    fallbackToLogin('blocked');
    return;
  }

  // window.open() with the same name reuses an existing popup. If the reused
  // popup navigated to OneLogin (cross-origin) during a prior incomplete login,
  // accessing popup.document throws a SecurityError and aborts the flow.
  // Close the stale popup and open a fresh one.
  try {
    void popup.document;
  } catch {
    popup.close();
    popup = openPopupTo('about:blank');
    if (!popup) {
      fallbackToLogin('blocked');
      return;
    }
  }

  try {
    const popupDoc = popup.document;
    buildLoadingContent(popupDoc);
  } catch {
    if (!popup.closed) popup.close();
    fallbackToLogin('flow_error');
    return;
  }

  attachMessageListener(popup);

  axiosInstance
    .get(LOGIN_URL)
    .then((response) => {
      if (popup.closed) return;
      sessionStorage.setItem('oauth_state', response.data.state);
      popup.location.href = response.data.url;
    })
    .catch(() => {
      if (!popup.closed) popup.close();
      fallbackToLogin('flow_error');
    });
};

export {LOGIN_SUCCESS_EVENT};
